import { useRef, useState } from 'react';
import styled, { css } from 'styled-components';
import { observer } from 'mobx-react-lite';
import { Tooltip } from 'react-tooltip';

import { useBRollUpload } from './BRollUploadContext';
import { BRollAsset } from '@src/stores-v2/BRollStore';
import { useClickAway } from '@src/utility/useClickAway';
import { handleDownloadMedia } from '@src/utility/general';
import applyImageOptimizations from '@src/utility/applyImageOptimizations';

import SpinningLoading from '../SpinningLoading';

import DeleteIcon from '@src/svgs/DeleteIcon';
import DownloadIcon from '@src/svgs/DownloadIcon';
import EllipsisIcon from '@src/svgs/EllipsisIcon';
import RefreshIcon from '@src/svgs/RefreshIcon';

type Props = {
  asset: BRollAsset;
};

const BRollUploadAsset = observer((props: Props) => {
  const { asset } = props;
  const { bRollService } = useBRollUpload();

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [description, setDescription] = useState(asset.description);
  const [processingState, setProcessingState] = useState<
    'positive' | 'negative' | null
  >(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const actionMenuIconRef = useClickAway(() => setIsMenuOpen(false));

  const renderContent = () => {
    if (asset.type === 'photo') {
      return <AssetImage src={applyImageOptimizations(asset.url)} />;
    }
    return <AssetVideo src={asset.url} />;
  };

  const handleSaveDescription = async () => {
    if (description.trim() === asset.description) return;
    setProcessingState('positive');
    await bRollService.updateAssetDescription(asset, description.trim());
    setProcessingState(null);
  };

  const handleDeleteAsset = async () => {
    setIsMenuOpen(false);
    setProcessingState('negative');
    await bRollService.removeAsset(asset.id);
    setProcessingState(null);
  };

  const handleDownloadAsset = async (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    if (asset.status === 'pending') return;
    setIsMenuOpen(false);
    setProcessingState('positive');
    await handleDownloadMedia(e, {
      url: asset.url,
      id: asset.id,
    });
    setProcessingState(null);
  };

  const handleRetryUpload = async () => {
    setProcessingState('positive');
    await bRollService.retryFailedAsset(asset);
    setProcessingState(null);
  };

  const isUploading = asset.status === 'uploading';
  const isFailed = asset.status === 'failed';
  const isDisabled = Boolean(processingState) || isUploading;

  return (
    <Container>
      <Header>
        <AssetTag type={asset.type}>{asset.type}</AssetTag>

        {isFailed && (
          <>
            <RetryUploadButton
              onClick={handleRetryUpload}
              data-tooltip-id="retry-upload-tooltip"
              data-tooltip-content="Failed to upload. Click to retry."
            >
              <RefreshIcon strokeColor="#ef5d6f" />
            </RetryUploadButton>
            <Tooltip
              id="retry-upload-tooltip"
              style={{
                padding: '8px',
                fontSize: '12px',
                lineHeight: '120%',
                zIndex: '10',
                fontWeight: 600,
              }}
              place="top"
              positionStrategy="fixed"
            />{' '}
          </>
        )}

        <ActionMenuIcon
          ref={actionMenuIconRef}
          isDisabled={isDisabled}
          onClick={isDisabled ? undefined : () => setIsMenuOpen(true)}
        >
          {processingState ? (
            <Spinner
              color={processingState === 'positive' ? '#17c964' : '#ef5d6f'}
            />
          ) : (
            <EllipsisIcon />
          )}
          {isMenuOpen && (
            <ActionMenu>
              {asset.status === 'uploaded' && (
                <ActionMenuItem onClick={handleDownloadAsset}>
                  Download
                  <DownloadIcon width="14" height="14" strokeColor="#f3e9d7" />
                </ActionMenuItem>
              )}
              <ActionMenuItem negative onClick={handleDeleteAsset}>
                Delete
                <DeleteIcon width="14" height="14" strokeColor="#f3e9d7" />
              </ActionMenuItem>
            </ActionMenu>
          )}
        </ActionMenuIcon>
      </Header>
      <Content>
        <ContentMedia muted={asset.status === 'pending'}>
          {renderContent()}
        </ContentMedia>
        {isUploading && (
          <SpinningLoading
            Ico={null}
            text="Uploading..."
            contentStyle={{ gap: '12px' }}
            spinnerStyle={{ width: '24px', height: '24px' }}
            spinnerCircleStyle={{ borderWidth: '3px' }}
            textStyle={{ fontSize: '12px' }}
          />
        )}
      </Content>
      <DescriptionTextarea
        ref={inputRef}
        value={description}
        disabled={Boolean(processingState)}
        onChange={(e) => setDescription(e.target.value)}
        onBlur={handleSaveDescription}
        placeholder="Add description..."
      />
    </Container>
  );
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  height: 28px;
  gap: 8px;
`;

const AssetTag = styled.div<{ type: 'photo' | 'video' }>`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  border: 1px solid
    ${(props) => (props.type === 'photo' ? '#17c964' : '#006fee')};
  color: ${(props) => (props.type === 'photo' ? '#17c964' : '#006fee')};
  font-size: 12px;
  font-weight: 500;
  text-transform: capitalize;
  margin-right: auto;
`;

const RetryUploadButton = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 24px;
  width: 24px;
  cursor: pointer;
  svg circle {
    transition: 0.2s ease;
  }
  &:hover svg circle {
    fill: #f2d093;
  }
`;

const ActionMenuIcon = styled.div<{ isDisabled?: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  height: 24px;
  width: 24px;
  cursor: pointer;
  svg circle {
    transition: 0.2s ease;
  }
  &:hover svg circle {
    fill: #f2d093;
  }
  ${(props) =>
    props.isDisabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}
`;

const ActionMenu = styled.div`
  position: absolute;
  top: 24px;
  right: 0;
  display: flex;
  flex-direction: column;
  background-color: #03041a;
  border-radius: 8px;
  border: 1px solid #484848;
  box-shadow: 8px 16px 8px 0px rgba(0, 0, 0, 0.4);
  overflow: hidden;
  z-index: 1;
`;

const ActionMenuItem = styled.div<{ negative?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 500;
  color: #f3e9d7;
  width: 112px;
  &:not(:last-child) {
    border-bottom: 1px solid #484848;
  }
  &,
  svg {
    transition: 0.2s ease;
  }
  &:hover {
    color: #03041a;
    background-color: ${(props) => (props.negative ? '#ef5d6f' : '#17c964')};
    svg path {
      stroke: #03041a;
    }
  }
`;

const Content = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const ContentMedia = styled.div<{ muted?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${(props) => (props.muted ? 0.4 : 1)};
`;

const AssetImage = styled.img`
  width: 100%;
  height: 144px;
  object-fit: cover;
  border-radius: 8px;
`;

const AssetVideo = styled.video`
  width: 100%;
  height: 144px;
  object-fit: cover;
  border-radius: 8px;
`;

const DescriptionTextarea = styled.textarea`
  height: 50px;
  border: 0;
  outline: 0;
  background: transparent;
  color: #f3e9d7;
  font-size: 12px;
  resize: none;
  padding: 4px;
  border: 1px solid transparent;
  border-radius: 8px;
  font-family: inherit;
  transition: 0.2s ease;
  &::placeholder {
    color: #848484;
  }
  &:focus {
    border-color: #484848;
  }
  &:disabled {
    color: #848484;
    cursor: not-allowed;
  }
`;

const Spinner = styled.div<{ color?: string }>`
  border: 2px solid #484848;
  border-radius: 50%;
  border-top: 2px solid ${(props) => props.color || '#17c964'};
  border-left: 2px solid ${(props) => props.color || '#17c964'};
  border-right: 2px solid ${(props) => props.color || '#17c964'};
  border-radius: 50%;
  width: 20px;
  height: 20px;
  animation: spinner 1s linear infinite;
  @keyframes spinner {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export default BRollUploadAsset;
